import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { startAuthentication } from '@simplewebauthn/browser';
import { useAuth } from '../contexts/AuthContext';
import { redirectToPhantomIfMobile } from '../utils/phantomMobile';
import { apiFetch, getApiHost } from '../utils/api';
import { trackEvent } from '../utils/analytics';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose }) => {
  const { setVisible: setWalletModalVisible } = useWalletModal();
  const { connected, publicKey, signMessage } = useWallet();
  const { isAuthenticated, loginWithWallet, loginWithEmail, refreshUser } = useAuth();
  const [loginError, setLoginError] = useState<string | null>(null);
  const [signingIn, setSigningIn] = useState(false);
  const [pendingLogin, setPendingLogin] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);

  // Email login state
  const [emailInput, setEmailInput] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const passkeysAvailable = typeof window !== 'undefined'
    && typeof navigator !== 'undefined'
    && 'credentials' in navigator
    && typeof (navigator.credentials as any)?.get === 'function';

  // Close modal if user becomes authenticated
  useEffect(() => {
    if (isAuthenticated) {
      setSigningIn(false);
      setPendingLogin(false);
      setLoginError(null);
      setEmailSent(false);
      setEmailInput('');
      onClose();
    }
  }, [isAuthenticated, onClose]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setLoginError(null);
      setEmailSent(false);
      setEmailInput('');
    }
  }, [isOpen]);

  // When wallet connects after user clicked "Connect Wallet", trigger sign-in
  const doLogin = useCallback(async () => {
    if (!connected || !publicKey || !signMessage || signingIn) return;
    setSigningIn(true);
    setLoginError(null);
    try {
      await loginWithWallet();
    } catch (err: any) {
      if (err?.message?.includes('User rejected') || err?.message?.includes('rejected')) {
        setLoginError('Sign request cancelled');
      } else {
        setLoginError(err?.message || 'Login failed. Please try again.');
      }
    } finally {
      setSigningIn(false);
      setPendingLogin(false);
    }
  }, [connected, publicKey, signMessage, signingIn, loginWithWallet]);

  // If wallet just connected and we have a pending login, trigger it
  useEffect(() => {
    if (pendingLogin && connected && publicKey && signMessage && !isAuthenticated) {
      doLogin();
    }
  }, [pendingLogin, connected, publicKey, signMessage, isAuthenticated, doLogin]);

  const handleWalletClick = () => {
    setLoginError(null);
    if (redirectToPhantomIfMobile()) return;
    if (connected && publicKey && signMessage) {
      doLogin();
    } else {
      setPendingLogin(true);
      setWalletModalVisible(true);
    }
  };

  const handlePasskeyClick = async () => {
    if (passkeyLoading) return;
    setPasskeyLoading(true);
    setLoginError(null);
    try {
      const beginRes = await apiFetch(`${getApiHost()}/api/auth/passkey/login/begin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!beginRes.ok) {
        const err = await beginRes.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to start passkey sign-in');
      }
      const options = await beginRes.json();

      const credential = await startAuthentication({ optionsJSON: options });

      const completeRes = await apiFetch(`${getApiHost()}/api/auth/passkey/login/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credential),
      });
      if (!completeRes.ok) {
        const err = await completeRes.json().catch(() => ({}));
        throw new Error(err.error || 'Passkey sign-in failed');
      }
      const data = await completeRes.json();
      if (!data.token) throw new Error('Sign-in did not return a token');
      localStorage.setItem('deepnets_token', data.token);
      trackEvent('login_completed', { method: 'passkey' });
      // Pull resolved user (tier/subscription/dnet); modal closes on isAuthenticated.
      await refreshUser();
    } catch (err: any) {
      const message = err?.message || 'Passkey sign-in failed';
      if (message.includes('cancel') || message.includes('timed out') || message.includes('NotAllowed')) {
        setLoginError('Cancelled.');
      } else {
        setLoginError(message);
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailInput.trim() || emailSending) return;

    setEmailSending(true);
    setLoginError(null);
    try {
      await loginWithEmail(emailInput.trim());
      setEmailSent(true);
      // The magic-link flow leaves the site here, and nothing recorded that it had
      // started: no client event, no server event, and email_tokens has a TTL index
      // that deletes the row 15 minutes later. Email is the #1 sign-in method, so
      // its drop-off was the largest unmeasurable step in the funnel. Pair with
      // email_verify_opened / login_completed to close it. No address is sent.
      trackEvent('email_link_requested');
    } catch (err: any) {
      setLoginError(err?.message || 'Failed to send sign-in link');
      trackEvent('email_link_failed', { reason: err?.message ? String(err.message).slice(0, 80) : 'unknown' });
    } finally {
      setEmailSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-crypto-gray border border-gray-700 rounded-xl p-6 w-full max-w-md mx-4 shadow-2xl">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Header — value-forward: say what logging in unlocks, and surface the
            free-credits hook (was buried as fine print at the bottom). */}
        <div className="text-center mb-5">
          <h2 className="text-xl font-bold text-white">See the full picture — free</h2>
          <p className="text-gray-400 text-sm mt-1.5">
            Unlock full holder analysis, safety history, and the trending feed.
          </p>
          <div className="inline-flex items-center gap-1.5 mt-3 text-xs font-medium text-crypto-accent bg-crypto-accent/10 border border-crypto-accent/25 rounded-full px-3 py-1">
            ✨ New accounts get free credits to start
          </div>
        </div>

        {emailSent ? (
          /* Email sent confirmation */
          <div className="text-center py-4">
            <div className="text-4xl mb-3">✉️</div>
            <h3 className="text-lg font-semibold text-white mb-2">Check your email</h3>
            <p className="text-gray-400 text-sm mb-4">
              We sent a sign-in link to <span className="text-white font-medium">{emailInput}</span>. Click it to sign in.
            </p>
            <p className="text-gray-500 text-xs">
              Didn't get it?{' '}
              <button
                onClick={() => { setEmailSent(false); setLoginError(null); }}
                className="text-crypto-accent hover:text-crypto-accent/80 transition-colors"
              >
                Try again
              </button>
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Email — the #1 sign-in method, so it leads and shows inline (no
                extra click to reveal). Magic link, no password. */}
            <form onSubmit={handleEmailSubmit} className="space-y-2">
              <input
                type="email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                placeholder="you@example.com"
                className="w-full bg-crypto-dark border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-crypto-accent transition-colors text-sm"
                required
              />
              <button
                type="submit"
                disabled={emailSending || !emailInput.trim()}
                className="w-full flex items-center justify-center gap-2 bg-crypto-accent hover:bg-crypto-accent/80 disabled:opacity-60 disabled:cursor-not-allowed text-crypto-dark font-medium py-3 px-4 rounded-lg transition-colors"
              >
                {emailSending ? (
                  <>
                    <div className="w-4 h-4 border-2 border-crypto-dark border-t-transparent rounded-full animate-spin" />
                    Sending…
                  </>
                ) : (
                  'Email me a sign-in link'
                )}
              </button>
            </form>

            {/* Divider */}
            <div className="flex items-center gap-3 py-1">
              <div className="flex-1 border-t border-gray-700"></div>
              <span className="text-gray-500 text-xs">or continue with</span>
              <div className="flex-1 border-t border-gray-700"></div>
            </div>

            {/* Wallet connect (secondary now — email converts better) */}
            <button
              onClick={handleWalletClick}
              disabled={signingIn}
              className="w-full flex items-center justify-center gap-3 bg-gray-700 hover:bg-gray-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-colors"
            >
              {signingIn ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Signing in…
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M21 18v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v1h-9a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h9zm-9-2h10V8H12v8zm4-2.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z" />
                  </svg>
                  {connected ? 'Sign In with Wallet' : 'Connect Wallet'}
                </>
              )}
            </button>

            {/* Passkey sign-in (only shown if WebAuthn is available) */}
            {passkeysAvailable && (
              <button
                onClick={handlePasskeyClick}
                disabled={passkeyLoading || signingIn}
                className="w-full flex items-center justify-center gap-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-colors"
              >
                {passkeyLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Follow prompts on your device…
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
                    </svg>
                    Sign in with Passkey
                  </>
                )}
              </button>
            )}

            {loginError && (
              <p className="text-center text-red-400 text-sm">{loginError}</p>
            )}

            <p className="text-center text-gray-500 text-xs mt-3">
              Email or passkey takes seconds. Connect a wallet anytime to unlock $DNET tier access.
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="mt-6 pt-4 border-t border-gray-700">
          <p className="text-center text-gray-500 text-xs">
            By signing in, you agree to our{' '}
            <Link to="/terms" onClick={onClose} className="text-crypto-accent hover:text-crypto-accent/80 transition-colors">
              Terms of Service
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginModal;
